import { ArrowUpRight, Github, Mail } from "lucide-react";
import { profile } from "../../data/portfolioData";

const ConsoleBlock = () => {
    return (
        <div
            data-testid="hero-terminal-block"
            className="relative overflow-hidden rounded-xl border border-[var(--ink-border)] bg-[var(--ink-surface-deep)] shadow-2xl shadow-black/40"
        >
            {/* terminal title bar */}
            <div className="flex items-center gap-2 border-b border-[var(--ink-border)] bg-[var(--ink-surface)] px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-[#3b3f47]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#3b3f47]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#3b3f47]" />
                <span className="ml-3 font-mono text-[11px] tracking-widest text-[var(--ink-text-secondary)]">
                    ~/vichu — zsh
                </span>
            </div>

            <div className="space-y-3 p-5 font-mono text-[13px] leading-relaxed sm:text-sm">
                <div>
                    <span className="text-ink-green">vichu@lab</span>
                    <span className="text-[var(--ink-text-secondary)]">:</span>
                    <span className="text-ink-cyan">~</span>
                    <span className="text-[var(--ink-text-secondary)]">$ </span>
                    <span className="text-[var(--ink-text-primary)]">whoami</span>
                </div>
                <div className="pl-2 text-[var(--ink-text-secondary)]">
                    vichu — software engineer, student, tinkerer
                </div>

                <div className="pt-2">
                    <span className="text-ink-green">vichu@lab</span>
                    <span className="text-[var(--ink-text-secondary)]">:</span>
                    <span className="text-ink-cyan">~</span>
                    <span className="text-[var(--ink-text-secondary)]">$ </span>
                    <span className="text-[var(--ink-text-primary)]">
                        cat focus.txt
                    </span>
                </div>
                <ul className="space-y-1 pl-2 text-[var(--ink-text-secondary)]">
                    <li>
                        <span className="text-ink-cyan">›</span> backend systems
                    </li>
                    <li>
                        <span className="text-ink-cyan">›</span> cloud
                        infrastructure
                    </li>
                    <li>
                        <span className="text-ink-cyan">›</span> ai tools &amp;
                        agents
                    </li>
                    <li>
                        <span className="text-ink-cyan">›</span> apps &amp;
                        product experiments
                    </li>
                    <li>
                        <span className="text-ink-cyan">›</span> stories about
                        weird tech
                    </li>
                </ul>

                <div className="pt-2">
                    <span className="text-ink-green">vichu@lab</span>
                    <span className="text-[var(--ink-text-secondary)]">:</span>
                    <span className="text-ink-cyan">~</span>
                    <span className="text-[var(--ink-text-secondary)]">$ </span>
                    <span className="text-[var(--ink-text-primary)]">
                        systemctl status
                    </span>
                </div>
                <div className="pl-2 text-[var(--ink-text-secondary)]">
                    <div>
                        <span className="text-ink-green">●</span> currently:{" "}
                        <span className="text-[var(--ink-text-primary)]">
                            building useful things from strange ideas
                        </span>
                    </div>
                    <div>
                        <span className="text-ink-green">●</span> mode:{" "}
                        <span className="text-[var(--ink-text-primary)]">
                            experimenting
                        </span>
                    </div>
                    <div>
                        <span className="text-ink-green">●</span> based:{" "}
                        <span className="text-[var(--ink-text-primary)]">
                            kerala, india
                        </span>
                    </div>
                </div>

                <div className="pt-2">
                    <span className="text-ink-green">vichu@lab</span>
                    <span className="text-[var(--ink-text-secondary)]">:</span>
                    <span className="text-ink-cyan">~</span>
                    <span className="text-[var(--ink-text-secondary)]">$ </span>
                    <span className="terminal-cursor" />
                </div>
            </div>
        </div>
    );
};

export const HeroSection = () => {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden pt-32 md:pt-40"
        >
            {/* faint grid background */}
            <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-40" />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-24 md:px-12 lg:grid-cols-12 lg:gap-10 lg:px-16 lg:pb-32">
                <div className="lg:col-span-7">
                    <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--ink-border)] bg-[var(--ink-surface)] px-3 py-1.5">
                        <span className="relative inline-flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ink-green opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-ink-green" />
                        </span>
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-text-secondary)]">
                            available · open to interesting work
                        </span>
                    </div>

                    <h1 className="text-4xl font-medium tracking-tight text-[var(--ink-text-primary)] sm:text-5xl lg:text-6xl">
                        Hi, I’m{" "}
                        <span className="relative whitespace-nowrap">
                            <span className="relative z-10 text-ink-cyan">
                                Vichu
                            </span>
                            <span className="absolute -bottom-1 left-0 z-0 h-2 w-full rounded-sm bg-ink-cyan/15" />
                        </span>
                        .
                    </h1>

                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[var(--ink-text-secondary)] md:text-lg">
                        {profile.heroIntro}
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <a
                            href="#projects"
                            data-testid="hero-cta-projects"
                            className="group inline-flex items-center gap-2 rounded-md bg-ink-cyan px-5 py-3 text-sm font-medium text-[#0B0F14] transition-transform hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] glow-cyan"
                        >
                            View Projects
                            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <a
                            href="#contact"
                            data-testid="hero-cta-contact"
                            className="inline-flex items-center gap-2 rounded-md border border-[var(--ink-border-strong)] bg-[var(--ink-surface)] px-5 py-3 text-sm text-[var(--ink-text-primary)] transition-colors hover:border-ink-cyan/40 hover:text-ink-cyan"
                        >
                            <Mail className="h-4 w-4" />
                            Contact Me
                        </a>
                        <a
                            href={profile.links.github}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-cta-github"
                            className="inline-flex items-center gap-2 rounded-md border border-[var(--ink-border)] bg-transparent px-5 py-3 text-sm text-[var(--ink-text-secondary)] transition-colors hover:border-ink-cyan/40 hover:text-ink-cyan"
                        >
                            <Github className="h-4 w-4" />
                            GitHub
                        </a>
                    </div>

                    {/* mini meta strip */}
                    <div className="mt-12 grid max-w-xl grid-cols-3 gap-6 border-t border-[var(--ink-border)] pt-6">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-text-secondary)]">
                                Based
                            </div>
                            <div className="mt-1 text-sm text-[var(--ink-text-primary)]">
                                Kerala, India
                            </div>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-text-secondary)]">
                                Mode
                            </div>
                            <div className="mt-1 text-sm text-[var(--ink-text-primary)]">
                                Building · Learning
                            </div>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-text-secondary)]">
                                Stack
                            </div>
                            <div className="mt-1 text-sm text-[var(--ink-text-primary)]">
                                Go · Cloud · AI
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5 lg:pt-2">
                    <ConsoleBlock />
                </div>
            </div>
        </section>
    );
};

export default HeroSection;
